import React from "react";
import { Button, Divider, Typography, ResponsiveImage } from "../common";
import { Lesson } from "../../types/newtypes";
import { Clock } from "lucide-react";
import { minutesToHoursAndMinutes } from "../../utils/progressUtils";

interface LessonIntroProps {
  lesson: Lesson;
  onStart: () => void;
  lessonRef?: React.RefObject<HTMLDivElement | null>;
  onSkip?: () => void;
  isSkipping?: boolean;
}

const LessonIntro: React.FC<LessonIntroProps> = ({
  lesson,
  onStart,
  onSkip,
  isSkipping = false,
}) => {
  const handleStart = () => {
    onStart();
  };

  return (
    <div className="flex flex-col w-full gap-6">
      {/* First Section - Title, Intro and Start Button */}
      <div className="h-[80vh] bg-white rounded-[10px] xl:p-12 lg:p-8 p-6 shadow-sm">
        <div className="w-full max-w-[1200px] lg:mx-auto">
          <div className="flex flex-col lg:items-center justify-between gap-8 lg:gap-20 lg:flex-row">
            <div className="flex-1 w:1/2">
              <div className="flex items-center gap-2">
                <Typography
                  variant="small"
                  weight="bold"
                  color="none"
                  className="text-slate-500 leading-[120%]"
                >
                  UNIT
                </Typography>
                {lesson.is_optional === true && (
                  <span
                    data-testid="lesson-optional-badge"
                    className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700"
                  >
                    Optional
                  </span>
                )}
                {lesson.is_optional === true &&
                  lesson.learner_progress?.skipped_at && (
                  <span
                    data-testid="lesson-skipped-chip"
                    className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
                  >
                      Skipped
                  </span>
                )}
                {lesson.learner_progress?.is_completed &&
                  !lesson.learner_progress?.skipped_at && (
                  <span
                    data-testid="lesson-completed-chip"
                    className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
                  >
                      Completed
                  </span>
                )}
              </div>
              <Typography variant="h1" weight="bold" className="mb-4">
                {lesson?.title}
              </Typography>
              <Typography
                variant="p"
                className="!leading-[150%] text-opacity-80 mb-6"
              >
                {lesson?.description}
              </Typography>
              <Divider />
              <div className="flex gap-4 md:gap-10 mt-6 flex-row items-center">
                <Button
                  variant="filled"
                  className="w-[175px] sm:w-[208px] h-11 !text-pd-cream hover:!text-pd-ink"
                  size="none"
                  onClick={handleStart}
                >
                  {lesson.learner_progress?.is_completed &&
                  !lesson.learner_progress?.skipped_at
                    ? "Review"
                    : "Start"}
                </Button>
                <div className="flex items-center gap-2 text-pd-fg-muted">
                  <Clock size={16} strokeWidth={1.5} color="rgb(var(--pd-ink))" />
                  <Typography variant="small">
                    {minutesToHoursAndMinutes(lesson?.estimated_duration_minutes ?? lesson?.completion_time ?? 0)}
                  </Typography>
                  {/* <span className="mx-4 text-textPrimary-60">•</span>
                  <ChartColumnIncreasing
                    size={16}
                    strokeWidth={1.5}
                    color="rgb(var(--pd-ink))"
                  />
                  <Typography variant="small">Beginner</Typography> */}
                </div>
              </div>
              {lesson.is_optional === true &&
                !lesson.learner_progress?.is_completed && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="none"
                    className="min-h-11 px-4"
                    onClick={onSkip}
                    disabled={!onSkip || isSkipping}
                    data-testid="lesson-skip-button"
                  >
                      Skip this unit - optional for non-teaching staff
                  </Button>
                  <p className="mt-2 text-sm text-slate-600">
                      Skipping counts this unit as complete for your progress and
                      certificate. You can come back and complete it at any time.
                  </p>
                </div>
              )}
              {lesson.is_optional === true &&
                lesson.learner_progress?.skipped_at && (
                <p className="mt-4 text-sm text-slate-600">
                    You skipped this unit. Completing it will replace the skip.
                </p>
              )}
            </div>
            <div className="lg:w-[480px] md:w-[50%] w-full h-auto self-center">
              {lesson?.icon && (
                <ResponsiveImage
                  image_uri={
                    lesson?.icon && typeof lesson?.icon === "string"
                      ? lesson.icon
                      : null
                  }
                  alt={lesson?.title || "Unit illustration"}
                  maxWidth="100%"
                  maxHeight="400px"
                  objectFit="contain"
                  containerClassName="rounded-lg"
                  placeholderText="Unit illustration not available"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonIntro;
